import { useState, useCallback } from 'react'
import ELK from 'elkjs/lib/elk.bundled.js'
import type { Node, Edge } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
import { useWorkflowStore } from '@/stores/workflow-store'

const elk = new ELK()

const elkOptions: Record<string, string> = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',
  'elk.spacing.nodeNode': '80',
}

/**
 * Compute positions for nodes using ELK layered layout algorithm.
 * Pure function — takes nodes/edges, returns repositioned nodes.
 */
export async function layoutGraph(
  nodes: Node[],
  edges: Edge[]
): Promise<Node[]> {
  if (nodes.length === 0) return nodes

  const graph = {
    id: 'root',
    layoutOptions: elkOptions,
    children: nodes.map((n) => ({
      id: n.id,
      width: n.measured?.width ?? 220,
      height: n.measured?.height ?? 80,
    })),
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  }

  const laid = await elk.layout(graph)

  return nodes.map((n) => {
    const laidNode = laid.children?.find((c) => c.id === n.id)
    if (!laidNode) return n
    return { ...n, position: { x: laidNode.x ?? 0, y: laidNode.y ?? 0 } }
  })
}

/**
 * Hook that wraps layoutGraph with React Flow integration.
 * Applies ELK layout and updates both React Flow and the workflow store.
 */
export function useElkLayout() {
  const [isLayouting, setIsLayouting] = useState(false)
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const flowEdges = useWorkflowStore((s) => s.flowEdges)
  const { setNodes } = useReactFlow()

  const applyLayout = useCallback(async () => {
    setIsLayouting(true)
    try {
      const laidOut = await layoutGraph(flowNodes, flowEdges)
      setNodes(laidOut)
    } finally {
      setIsLayouting(false)
    }
  }, [flowNodes, flowEdges, setNodes])

  return { applyLayout, isLayouting }
}
